import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Plus, X, Upload, Sparkles } from 'lucide-react'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import useRecipeStore from '../store/recipes'
import useAuthStore from '../store/auth'

const CreateRecipePage = () => {
  const { createRecipe, isLoading } = useRecipeStore()
  const { isAuthenticated } = useAuthStore()

  const [recipe, setRecipe] = useState({
    title: '',
    description: '',
    cookTime: '',
    servings: '',
    difficulty: 'easy',
    category: 'main',
    ingredients: [''],
    steps: [''],
    image: null
  })

  const [imagePreview, setImagePreview] = useState(null)

  if (!isAuthenticated) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">🔒</div>
        <h2 className="text-2xl font-display font-bold text-gray-900 mb-4">
          Sign in to create recipes
        </h2>
        <p className="text-gray-600 mb-6">
          Join our magical community to share your favorite recipes
        </p>
        <Button asChild>
          <Link to="/login">Sign In</Link>
        </Button>
      </div>
    )
  }

  const handleInputChange = (field, value) => {
    setRecipe(prev => ({ ...prev, [field]: value }))
  }

  const handleArrayChange = (field, index, value) => {
    setRecipe(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }))
  }

  const addArrayItem = (field) => {
    setRecipe(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }))
  }

  const removeArrayItem = (field, index) => {
    setRecipe(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }))
  }

  const handleImageUpload = (e) => {
    const file = e.target.files[0]
    if (file) {
      setRecipe(prev => ({ ...prev, image: file }))

      const reader = new FileReader()
      reader.onload = (e) => setImagePreview(e.target.result)
      reader.readAsDataURL(file)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    try {
      const formData = new FormData()
      Object.keys(recipe).forEach(key => {
        if (key === 'ingredients' || key === 'steps') {
          formData.append(key, JSON.stringify(recipe[key].filter(item => item.trim())))
        } else if (key === 'image' && recipe[key]) {
          formData.append(key, recipe[key])
        } else {
          formData.append(key, recipe[key])
        }
      })

      await createRecipe(formData)
      // Redirect to recipe detail or recipes page
    } catch (error) {
      console.error('Failed to create recipe:', error)
    }
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="text-center">
        <h1 className="text-3xl font-display font-bold text-dreamy mb-2">
          Create Your Magical Recipe
        </h1>
        <p className="text-gray-600">
          Share your culinary dreams with the world
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle>Recipe Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Recipe Title
              </label>
              <Input
                placeholder="Enter a magical recipe name..."
                value={recipe.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                className="input-dreamy min-h-[80px] resize-none"
                placeholder="Describe what makes this recipe special..."
                value={recipe.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cook Time (minutes)
                </label>
                <Input
                  type="number"
                  placeholder="30"
                  value={recipe.cookTime}
                  onChange={(e) => handleInputChange('cookTime', e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Servings
                </label>
                <Input
                  type="number"
                  placeholder="4"
                  value={recipe.servings}
                  onChange={(e) => handleInputChange('servings', e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Difficulty
                </label>
                <select
                  className="input-dreamy"
                  value={recipe.difficulty}
                  onChange={(e) => handleInputChange('difficulty', e.target.value)}
                >
                  <option value="easy">Easy</option>
                  <option value="medium">Medium</option>
                  <option value="hard">Hard</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  className="input-dreamy"
                  value={recipe.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                >
                  <option value="breakfast">Breakfast</option>
                  <option value="lunch">Lunch</option>
                  <option value="dinner">Dinner</option>
                  <option value="dessert">Dessert</option>
                  <option value="snacks">Snacks</option>
                  <option value="healthy">Healthy</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Image Upload */}
        <Card>
          <CardHeader>
            <CardTitle>Recipe Image</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="border-2 border-dashed border-gray-300 rounded-2xl p-6 text-center hover:border-primary-500 transition-colors">
              {imagePreview ? (
                <div className="relative">
                  <img
                    src={imagePreview}
                    alt="Recipe preview"
                    className="mx-auto max-h-48 rounded-xl"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={() => {
                      setImagePreview(null)
                      setRecipe(prev => ({ ...prev, image: null }))
                    }}
                  >
                    Remove Image
                  </Button>
                </div>
              ) : (
                <label className="cursor-pointer">
                  <Upload className="mx-auto w-8 h-8 text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600 mb-2">
                    Upload a beautiful photo of your recipe
                  </p>
                  <Button type="button" variant="outline" size="sm">
                    Choose Image
                  </Button>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageUpload}
                  />
                </label>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Ingredients */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Ingredients
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => addArrayItem('ingredients')}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {recipe.ingredients.map((ingredient, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex gap-2"
              >
                <Input
                  placeholder={`Ingredient ${index + 1}`}
                  value={ingredient}
                  onChange={(e) => handleArrayChange('ingredients', index, e.target.value)}
                  className="flex-1"
                />
                {recipe.ingredients.length > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => removeArrayItem('ingredients', index)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </motion.div>
            ))}
          </CardContent>
        </Card>

        {/* Steps */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Instructions
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => addArrayItem('steps')}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {recipe.steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex gap-2"
              >
                <div className="flex-shrink-0 w-8 h-8 bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-medium mt-1">
                  {index + 1}
                </div>
                <textarea
                  className="input-dreamy flex-1 min-h-[60px] resize-none"
                  placeholder={`Step ${index + 1}`}
                  value={step}
                  onChange={(e) => handleArrayChange('steps', index, e.target.value)}
                />
                {recipe.steps.length > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => removeArrayItem('steps', index)}
                    className="mt-1"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </motion.div>
            ))}
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex gap-4 pt-6">
          <Button
            type="submit"
            size="lg"
            className="flex-1"
            disabled={isLoading}
            animation="sparkle"
          >
            <Sparkles className="w-5 h-5 mr-2" />
            {isLoading ? 'Creating Magic...' : 'Create Recipe'}
          </Button>
          <Button variant="outline" size="lg" asChild>
            <Link to="/recipes">Cancel</Link>
          </Button>
        </div>
      </form>
    </div>
  )
}

export default CreateRecipePage